import React, { useState, useEffect } from "react";
import { settings } from "../../helpers/settings";
import authHeader from "../../helpers/functions/auth-header";
import axios from "axios";
import Lottie from "lottie-react";
import { toast } from "../../helpers/swal";
import loadingAnim from "../../helpers/animations/loading-pulse.json";
import confettiAnim from "../../helpers/animations/confetti.json";
import "../../assets/css/TicketsPage.css";
import Sidebar from "../../components/common/sidebar/sidebar";

const TicketsPage = () => {
  const [activeTab, setActiveTab] = useState("my_tickets");
  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [tabCounts, setTabCounts] = useState({
    my_tickets: 0,
    assigned_tickets: 0,
  });

  const [allTickets, setAllTickets] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [isSpecialUser, setIsSpecialUser] = useState(false);
  const [newTicket, setNewTicket] = useState({
    order_number: "",
    description: "",
    issue_type: "",
    assigned_to: "",
  });
  const [orderInfo, setOrderInfo] = useState(null);
  const [orderLoading, setOrderLoading] = useState(false);
  const [activeStatus, setActiveStatus] = useState("all");
  const [ticketOwnership, setTicketOwnership] = useState("my_tickets");

  const specialUsers = [
    "sadivolkanavcii@gmail.com",
    "bariskocahasanoglu@icloud.com",
    "arif@txmaya.com",
    "mhmtmnctl20@gmail.com",
  ];

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchTickets(),
        fetchTabCounts(),
        fetchCurrentUser(),
        fetchUsers(),
        fetchAllTickets(),
      ]);
    } catch (error) {
      toast("Error loading data", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllTickets = async () => {
    try {
      const response = await axios.get(
        `${settings.apiURL}/get_tickets.php?type=all`,
        { headers: authHeader() }
      );
      if (response.data.success) {
        setAllTickets(response.data.tickets);
      }
    } catch (error) {
      console.error("Error fetching all tickets:", error);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const userEmail = localStorage.getItem("email");
      if (userEmail) {
        setCurrentUser({ email: userEmail });
        setIsSpecialUser(specialUsers.includes(userEmail));
      }
    } catch (error) {
      console.error("Error fetching current user:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(
        `${settings.apiURL}/get_users_for_tickets.php`,
        { headers: authHeader() }
      );
      if (response.data.success) {
        const sortedUsers = response.data.users.sort((a, b) => {
          const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
          const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setUsers(sortedUsers);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchOrderInfo = async (orderNumber) => {
    if (!orderNumber.trim()) {
      setOrderInfo(null);
      return;
    }

    try {
      setOrderLoading(true);
      const response = await axios.get(
        `${settings.apiURL}/get_order_info.php?order_number=${orderNumber}`,
        { headers: authHeader() }
      );

      if (response.data.success) {
        setOrderInfo(response.data.order);
      } else {
        setOrderInfo(null);
        toast(response.data.message || "Order not found", "warning");
      }
    } catch (error) {
      console.error("Error fetching order info:", error);
      setOrderInfo(null);
      toast("Error fetching order information", "error");
    } finally {
      setOrderLoading(false);
    }
  };

  const handleOrderSearch = async () => {
    if (!newTicket.order_number.trim()) {
      toast("Please enter order number", "warning");
      return;
    }

    await fetchOrderInfo(newTicket.order_number);
  };

  const handleOrderNumberChange = (orderNumber) => {
    setNewTicket((prev) => ({ ...prev, order_number: orderNumber }));
    setOrderInfo(null);
  };

  const fetchTickets = async (ownership = null, status = null) => {
    try {
      const type = ownership || ticketOwnership;
      const apiType = type === "my_tickets" ? "created" : "assigned";
      const statusParam = status && status !== "all" ? `&status=${status}` : "";

      const response = await axios.get(
        `${settings.apiURL}/get_tickets.php?type=${apiType}${statusParam}`,
        { headers: authHeader() }
      );
      if (response.data.success) {
        setTickets(response.data.tickets);
      }
    } catch (error) {
      console.error("Error fetching tickets:", error);
    }
  };

  const fetchTabCounts = async () => {
    try {
      const [myTicketsResponse, assignedTicketsResponse] = await Promise.all([
        axios.get(
          `${settings.apiURL}/get_tickets.php?type=created&count_only=true`,
          { headers: authHeader() }
        ),
        axios.get(
          `${settings.apiURL}/get_tickets.php?type=assigned&count_only=true`,
          { headers: authHeader() }
        ),
      ]);

      if (
        myTicketsResponse.data.success &&
        assignedTicketsResponse.data.success
      ) {
        setTabCounts({
          my_tickets: myTicketsResponse.data.total_count || 0,
          assigned_tickets: assignedTicketsResponse.data.total_count || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching tab counts:", error);
    }
  };

  const fetchTicketDetail = async (ticketId) => {
    try {
      const response = await axios.get(
        `${settings.apiURL}/get_ticket_detail.php?ticket_id=${ticketId}`,
        { headers: authHeader() }
      );
      if (response.data.success) {
        setSelectedTicket(response.data);
        setShowTicketModal(true);
      }
    } catch (error) {
      console.error("Error fetching ticket detail:", error);
      toast("Error loading ticket details", "error");
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();

    if (!newTicket.order_number.trim()) {
      toast("Please enter order number", "warning");
      return;
    }

    if (isSpecialUser && !newTicket.assigned_to) {
      toast("Please select a user to assign", "warning");
      return;
    }

    if (!isSpecialUser && !newTicket.issue_type) {
      toast("Please select an issue type", "warning");
      return;
    }

    try {
      const response = await axios.post(
        `${settings.apiURL}/create_ticket.php`,
        newTicket,
        { headers: authHeader() }
      );

      if (response.data.success) {
        toast("Ticket created successfully!", "success");

        setNewTicket({
          order_number: "",
          description: "",
          issue_type: "",
          assigned_to: "",
        });
        setOrderInfo(null);

        setTicketOwnership("my_tickets");
        setActiveStatus("all");

        setTimeout(() => {
          fetchTickets("my_tickets", "all");
          fetchTabCounts();
        }, 100);
      } else {
        toast(response.data.message || "Failed to create ticket", "error");
      }
    } catch (error) {
      console.error("Error creating ticket:", error);
      toast("Failed to create ticket", "error");
    }
  };

  const handleAddMessage = async () => {
    if (!newMessage.trim()) {
      toast("Please enter a message", "warning");
      return;
    }

    try {
      const response = await axios.post(
        `${settings.apiURL}/add_ticket_message.php`,
        {
          ticket_id: selectedTicket.ticket.id,
          message: newMessage,
        },
        { headers: authHeader() }
      );

      if (response.data.success) {
        setNewMessage("");
        fetchTicketDetail(selectedTicket.ticket.id);
        fetchTickets();
        fetchTabCounts();
        toast("Message sent!", "success");
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toast("Failed to send message", "error");
    }
  };

  const handleUpdateStatus = async (ticketId, status) => {
    try {
      const response = await axios.post(
        `${settings.apiURL}/update_ticket_status.php`,
        {
          ticket_id: ticketId,
          status: status,
        },
        { headers: authHeader() }
      );

      if (response.data.success) {
        toast("Status updated successfully!", "success");
        fetchTickets();
        fetchTabCounts();
        if (selectedTicket) {
          fetchTicketDetail(selectedTicket.ticket.id);
        }

        if (status === "resolved") {
          setShowConfetti(true);
          setTimeout(() => setShowConfetti(false), 3000);
        }
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast("Failed to update status", "error");
    }
  };

  const handleDeleteTicket = async (ticketId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this ticket? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      const response = await axios.post(
        `${settings.apiURL}/delete_ticket.php`,
        {
          ticket_id: ticketId,
        },
        { headers: authHeader() }
      );

      if (response.data.success) {
        toast("Ticket deleted successfully!", "success");
        fetchTickets();
        fetchTabCounts();
        fetchAllTickets();
        if (selectedTicket && selectedTicket.ticket.id === ticketId) {
          setShowTicketModal(false);
          setSelectedTicket(null);
        }
      } else {
        toast(response.data.message || "Failed to delete ticket", "error");
      }
    } catch (error) {
      console.error("Error deleting ticket:", error);
      toast("Failed to delete ticket", "error");
    }
  };

  const handleStatusFilter = (status) => {
    setActiveStatus(status);
    fetchTickets(ticketOwnership, status);
  };

  const handleOwnershipFilter = (ownership) => {
    setTicketOwnership(ownership);
    setActiveStatus("all");
    fetchTickets(ownership, "all");
  };

  const handleCreateTicketClick = () => {
    setActiveTab("create_ticket");
  };

  const handleBackToList = () => {
    setActiveTab("list");
    fetchTickets(ticketOwnership, activeStatus);
  };

  useEffect(() => {
    if (!loading && activeTab === "list") {
      fetchTickets(ticketOwnership, activeStatus);
    }
  }, [ticketOwnership, activeStatus, activeTab]);

  const stats = {
    total: allTickets.length,
    open: allTickets.filter((t) => t.status === "open").length,
    in_progress: allTickets.filter((t) => t.status === "in_progress").length,
    closed: allTickets.filter((t) => t.status === "closed").length,
    resolved: allTickets.filter((t) => t.status === "resolved").length,
    exceeded_24h: allTickets.filter((t) => {
      if (t.status === "resolved") return false;
      const created = new Date(t.created_at);
      const now = new Date();
      const diffHours = (now - created) / (1000 * 60 * 60);
      return diffHours > 24;
    }).length,
  };

  if (loading) {
    return (
      <div className="tickets-loading-container">
        <div className="tickets-loading-content">
          <div className="tickets-lottie-container">
            <Lottie animationData={loadingAnim} loop={true} />
          </div>
          <p className="tickets-loading-text">Loading tickets...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex" }}>
      <Sidebar />

      <div
        className="tickets-container"
        style={{ flex: 1, marginLeft: "250px" }}
      >
        {showConfetti && (
          <div className="tickets-confetti-overlay">
            <Lottie animationData={confettiAnim} loop={false} />
          </div>
        )}

        <div className="tickets-content">
          <header className="tickets-header">
            <div className="tickets-header-top">
              <button
                onClick={() => (window.location.href = "/home")}
                className="tickets-home-button"
              >
                ← Home
              </button>
              <button
                onClick={fetchAllData}
                className="tickets-refresh-button"
                disabled={loading}
              >
                {loading ? "🔄 Refreshing..." : "🔄 Refresh"}
              </button>
            </div>
            <h1 className="tickets-title">Ticket System</h1>
            <p className="tickets-subtitle">
              Manage your support tickets and issues
            </p>
          </header>

          <div className="tickets-stats-grid">
            <StatCard
              icon="📊"
              title="Total"
              value={stats.total}
              color="blue"
            />
            <StatCard icon="🔴" title="Open" value={stats.open} color="red" />
            <StatCard
              icon="🔄"
              title="In Progress"
              value={stats.in_progress}
              color="yellow"
            />
            <StatCard
              icon="🟡"
              title="Closed"
              value={stats.closed}
              color="orange"
            />
            <StatCard
              icon="✅"
              title="Resolved"
              value={stats.resolved}
              color="green"
            />
          </div>

          <div className="tickets-quick-filters">
            <div className="tickets-ownership-filter">
              <button
                className={`tickets-filter-btn ${
                  ticketOwnership === "my_tickets" ? "active" : ""
                }`}
                onClick={() => handleOwnershipFilter("my_tickets")}
              >
                📨 My Tickets ({tabCounts.my_tickets})
              </button>
              <button
                className={`tickets-filter-btn ${
                  ticketOwnership === "assigned_tickets" ? "active" : ""
                }`}
                onClick={() => handleOwnershipFilter("assigned_tickets")}
              >
                👥 Assigned To Me ({tabCounts.assigned_tickets})
              </button>
              <button
                className={`tickets-filter-btn ${
                  activeTab === "create_ticket" ? "active" : ""
                }`}
                onClick={handleCreateTicketClick}
              >
                ➕ Create New Ticket
              </button>
            </div>

            {activeTab !== "create_ticket" && (
              <div className="tickets-status-filter">
                <button
                  className={`tickets-status-btn ${
                    activeStatus === "all" ? "active" : ""
                  }`}
                  onClick={() => handleStatusFilter("all")}
                >
                  All
                </button>
                <button
                  className={`tickets-status-btn ${
                    activeStatus === "open" ? "active" : ""
                  }`}
                  onClick={() => handleStatusFilter("open")}
                >
                  🔴 Open
                </button>
                <button
                  className={`tickets-status-btn ${
                    activeStatus === "in_progress" ? "active" : ""
                  }`}
                  onClick={() => handleStatusFilter("in_progress")}
                >
                  🔄 In Progress
                </button>
                <button
                  className={`tickets-status-btn ${
                    activeStatus === "closed" ? "active" : ""
                  }`}
                  onClick={() => handleStatusFilter("closed")}
                >
                  🟡 Closed
                </button>
                <button
                  className={`tickets-status-btn ${
                    activeStatus === "resolved" ? "active" : ""
                  }`}
                  onClick={() => handleStatusFilter("resolved")}
                >
                  ✅ Resolved
                </button>
              </div>
            )}
          </div>

          <div className="tickets-tab-content">
            {activeTab === "create_ticket" ? (
              <div>
                <div className="tickets-back-button-container">
                  <button
                    onClick={handleBackToList}
                    className="tickets-back-button"
                  >
                    ← Back to Tickets
                  </button>
                </div>
                <CreateTicketForm
                  newTicket={newTicket}
                  setNewTicket={setNewTicket}
                  onSubmit={handleCreateTicket}
                  isSpecialUser={isSpecialUser}
                  users={users}
                  orderInfo={orderInfo}
                  orderLoading={orderLoading}
                  onOrderNumberChange={handleOrderNumberChange}
                  onOrderSearch={handleOrderSearch}
                />
              </div>
            ) : (
              <TicketList
                tickets={tickets}
                type={ticketOwnership}
                onTicketClick={fetchTicketDetail}
                onUpdateStatus={handleUpdateStatus}
                onDeleteTicket={handleDeleteTicket}
              />
            )}
          </div>
        </div>

        {showTicketModal && selectedTicket && (
          <TicketDetailModal
            ticketData={selectedTicket}
            newMessage={newMessage}
            setNewMessage={setNewMessage}
            onAddMessage={handleAddMessage}
            onUpdateStatus={handleUpdateStatus}
            onDeleteTicket={handleDeleteTicket}
            onRefresh={() => fetchTicketDetail(selectedTicket.ticket.id)}
            onClose={() => {
              setShowTicketModal(false);
              setSelectedTicket(null);
              setNewMessage("");
            }}
          />
        )}
      </div>
    </div>
  );
};

const StatCard = ({ icon, title, value, color }) => {
  const colorClasses = {
    blue: "tickets-stat-card-blue",
    red: "tickets-stat-card-red",
    yellow: "tickets-stat-card-yellow",
    green: "tickets-stat-card-green",
    orange: "tickets-stat-card-orange",
  };

  return (
    <div className={`tickets-stat-card ${colorClasses[color]}`}>
      <div className="tickets-stat-content">
        <span className="tickets-stat-icon">{icon}</span>
        <div className="tickets-stat-text">
          <div className="tickets-stat-value">{value}</div>
          <div className="tickets-stat-label">{title}</div>
        </div>
      </div>
    </div>
  );
};

const calculateTicketDuration = (createdAt, resolvedAt) => {
  if (!resolvedAt) return null;

  const created = new Date(createdAt);
  const resolved = new Date(resolvedAt);
  const diffMs = resolved - created;

  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m`;
  } else if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else if (minutes > 0) {
    return `${minutes}m`;
  } else {
    return "Less than 1m";
  }
};

const isResolvedWithin24h = (createdAt, resolvedAt) => {
  if (!resolvedAt) return false;

  const created = new Date(createdAt);
  const resolved = new Date(resolvedAt);
  const diffMs = resolved - created;
  const diffHours = diffMs / (1000 * 60 * 60);

  return diffHours <= 24;
};

const isExceeded24h = (createdAt, status) => {
  if (status === "resolved") return false;

  const created = new Date(createdAt);
  const now = new Date();
  const diffMs = now - created;
  const diffHours = diffMs / (1000 * 60 * 60);

  return diffHours > 24;
};

const TicketList = ({
  tickets,
  type,
  onTicketClick,
  onUpdateStatus,
  onDeleteTicket,
}) => {
  return (
    <div className="tickets-list-container">
      <div className="tickets-list">
        {tickets.length === 0 ? (
          <div className="tickets-empty-state">
            <p>No tickets found</p>
          </div>
        ) : (
          tickets.map((ticket) => (
            <TicketCard
              key={ticket.id}
              ticket={ticket}
              type={type}
              onTicketClick={onTicketClick}
              onUpdateStatus={onUpdateStatus}
              onDeleteTicket={onDeleteTicket}
            />
          ))
        )}
      </div>
    </div>
  );
};

const TicketCard = ({
  ticket,
  type,
  onTicketClick,
  onUpdateStatus,
  onDeleteTicket,
}) => {
  const getStatusColor = (status) => {
    switch (status) {
      case "open":
        return "#ef4444";
      case "in_progress":
        return "#f59e0b";
      case "closed":
        return "#8b5cf6";
      case "resolved":
        return "#10b981";
      default:
        return "#6b7280";
    }
  };

  const getIssueTypeLabel = (issueType) => {
    if (!issueType) {
      return "Manual Assignment";
    }

    const issueTypes = {
      tshirt_related_issues: "Tshirt Related Issues",
      dtf_related_issues: "DTF Related Issues",
      dtf_label_related_issues: "DTF Label Related Issues",
      tshirt_label_related_issues: "Tshirt Label Related Issues",
      other_issues: "Other Issues",
      manual_assignment: "Manual Assignment",
      special_assignment: "Special Assignment",
    };

    return issueTypes[issueType] || issueType;
  };

  const ticketDuration = calculateTicketDuration(
    ticket.created_at,
    ticket.resolved_at
  );

  const resolvedWithin24h = isResolvedWithin24h(
    ticket.created_at,
    ticket.resolved_at
  );
  const exceeded24h = isExceeded24h(ticket.created_at, ticket.status);

  const currentUserEmail = localStorage.getItem("email");
  const canDelete =
    currentUserEmail === ticket.creator_email ||
    currentUserEmail === "mhmtmnctl@txmaya.com" ||
    currentUserEmail === "mhmtmnctl20@gmail.com";

  return (
    <div
      className={`tickets-card ${exceeded24h ? "tickets-card-urgent" : ""}`}
      data-status={ticket.status}
      onClick={() => onTicketClick(ticket.id)}
    >
      <div className="tickets-card-header">
        <h3 className="tickets-card-title">Order: {ticket.order_number}</h3>
        <div className="tickets-card-header-actions">
          {exceeded24h && (
            <span className="tickets-urgent-badge">24H+ URGENT</span>
          )}
          {ticket.status === "resolved" && !resolvedWithin24h && (
            <span className="tickets-warning-badge">24H+ Resolved</span>
          )}
          {canDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDeleteTicket(ticket.id);
              }}
              className="tickets-delete-button"
              title="Delete ticket"
            >
              🗑️
            </button>
          )}
        </div>
      </div>

      <div className="tickets-card-content">
        <p className="tickets-card-description">{ticket.description}</p>

        <div className="tickets-card-meta">
          <span className="tickets-card-category">
            {getIssueTypeLabel(ticket.issue_type)}
          </span>
          <span
            className="tickets-card-status"
            style={{ backgroundColor: getStatusColor(ticket.status) }}
          >
            {ticket.status.replace("_", " ")}
          </span>
        </div>

        <div className="tickets-card-info">
          <div className="tickets-card-assignment">
            {type === "my_tickets" ? (
              <span>
                To: {ticket.assigned_first_name} {ticket.assigned_last_name}
              </span>
            ) : (
              <span>
                From: {ticket.creator_first_name} {ticket.creator_last_name}
              </span>
            )}
          </div>
          <div className="tickets-card-dates">
            <span>Created: {new Date(ticket.created_at).toLocaleString()}</span>
            {ticketDuration && (
              <span
                className={`tickets-duration ${
                  resolvedWithin24h ? "duration-fast" : "duration-slow"
                }`}
              >
                Resolved in: {ticketDuration}
                {!resolvedWithin24h && " ⚠️"}
              </span>
            )}
            {ticket.message_count > 0 && (
              <span>Messages: {ticket.message_count}</span>
            )}
          </div>
        </div>
      </div>

      <div className="tickets-card-actions">
        {type === "assigned_tickets" && (
          <>
            {ticket.status === "open" && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onUpdateStatus(ticket.id, "in_progress");
                }}
                className="tickets-action-button tickets-action-primary"
              >
                Start Working
              </button>
            )}
            {ticket.status === "in_progress" && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onUpdateStatus(ticket.id, "closed");
                }}
                className="tickets-action-button tickets-action-warning"
              >
                Mark Closed
              </button>
            )}
          </>
        )}

        {type === "my_tickets" && ticket.status === "closed" && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onUpdateStatus(ticket.id, "resolved");
            }}
            className="tickets-action-button tickets-action-success"
          >
            Confirm Resolved
          </button>
        )}
      </div>
    </div>
  );
};

const OrderInfo = ({ orderInfo, loading }) => {
  if (loading) {
    return (
      <div className="order-info-loading">
        <p>Loading order information...</p>
      </div>
    );
  }

  if (!orderInfo) {
    return (
      <div className="order-info-placeholder">
        <p>Enter order number and click search to view order details</p>
      </div>
    );
  }

  const orderData = orderInfo.data;
  const userInfo = orderInfo.user_info;

  if (!orderData) {
    return (
      <div className="order-info-error">
        <p>Order data not available</p>
      </div>
    );
  }

  return (
    <div className="order-info-card">
      <h3 className="order-info-title">Order Information</h3>

      {userInfo && (
        <div className="store-owner-section">
          <h4>Store Owner</h4>
          <div className="store-owner-info">
            <strong>
              {userInfo.first_name} {userInfo.last_name}
            </strong>
            <span>{userInfo.email}</span>
          </div>
        </div>
      )}

      <div className="order-details-section">
        <h4>Order Details</h4>
        <div className="order-details-grid">
          <div className="order-detail-item">
            <label>Order Number:</label>
            <span>{orderData.orderNumber || orderInfo.order_number}</span>
          </div>
          <div className="order-detail-item">
            <label>Customer:</label>
            <span>{orderData.shipTo?.name || "N/A"}</span>
          </div>
          <div className="order-detail-item">
            <label>Order Date:</label>
            <span>{new Date(orderData.orderDate).toLocaleDateString()}</span>
          </div>
          <div className="order-detail-item">
            <label>Status:</label>
            <span className={`status-badge status-${orderData.orderStatus}`}>
              {orderData.orderStatus}
            </span>
          </div>
          <div className="order-detail-item">
            <label>Shipping:</label>
            <span>{orderData.serviceCode}</span>
          </div>
          <div className="order-detail-item">
            <label>Total:</label>
            <span>${orderData.orderTotal}</span>
          </div>
        </div>
      </div>

      <div className="order-items-section">
        <h4>Items</h4>
        <div className="order-items-list">
          {orderData.items &&
            orderData.items
              .filter((item) => !item.adjustment)
              .map((item, index) => (
                <div key={index} className="order-item">
                  <div className="order-item-image">
                    {item.imageUrl && (
                      <img
                        src={item.imageUrl}
                        alt={item.name}
                        className="item-image"
                      />
                    )}
                  </div>
                  <div className="order-item-details">
                    <div className="order-item-name">{item.name}</div>
                    <div className="order-item-meta">
                      <span>SKU: {item.sku}</span>
                      <span>Qty: {item.quantity}</span>
                      <span>${item.unitPrice}</span>
                    </div>
                    {item.options && item.options.length > 0 && (
                      <div className="order-item-options">
                        {item.options.map((option, optIndex) => (
                          <div key={optIndex} className="order-item-option">
                            <strong>{option.name}:</strong> {option.value}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
        </div>
      </div>
    </div>
  );
};

const CreateTicketForm = ({
  newTicket,
  setNewTicket,
  onSubmit,
  isSpecialUser,
  users,
  orderInfo,
  orderLoading,
  onOrderNumberChange,
  onOrderSearch,
}) => {
  const issueTypes = [
    { value: "tshirt_related_issues", label: "Tshirt Related Issues" },
    { value: "dtf_related_issues", label: "DTF Related Issues" },
    { value: "dtf_label_related_issues", label: "DTF Label Related Issues" },
    {
      value: "tshirt_label_related_issues",
      label: "Tshirt Label Related Issues",
    },
    { value: "other_issues", label: "Other Issues" },
  ];

  const handleChange = (field, value) => {
    setNewTicket((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!newTicket.order_number.trim()) {
      toast("Please enter order number", "warning");
      return;
    }

    if (isSpecialUser && !newTicket.assigned_to) {
      toast("Please select a user to assign", "warning");
      return;
    }

    if (!isSpecialUser && !newTicket.issue_type) {
      toast("Please select an issue type", "warning");
      return;
    }

    onSubmit(e);
  };

  return (
    <div className="create-ticket-page">
      <div className="create-ticket-layout">
        <div className="ticket-form-container">
          <div className="ticket-form-card">
            <h2 className="ticket-form-title">Create New Ticket</h2>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Order Number *</label>
                <div className="order-search-container">
                  <input
                    type="text"
                    value={newTicket.order_number}
                    onChange={(e) => onOrderNumberChange(e.target.value)}
                    placeholder="Enter order number"
                    className="form-input order-number-input"
                    required
                  />
                  <button
                    type="button"
                    onClick={onOrderSearch}
                    disabled={orderLoading || !newTicket.order_number.trim()}
                    className="order-search-button"
                  >
                    {orderLoading ? "🔍 Searching..." : "🔍 Search"}
                  </button>
                </div>
                {orderInfo && (
                  <div className="order-search-success">
                    ✅ Order found:{" "}
                    {orderInfo.order_number || orderInfo.data?.orderNumber}
                  </div>
                )}
              </div>

              {isSpecialUser ? (
                <div className="form-group">
                  <label className="form-label">Assign To *</label>
                  <select
                    value={newTicket.assigned_to}
                    onChange={(e) =>
                      handleChange("assigned_to", e.target.value)
                    }
                    className="form-select"
                    required
                  >
                    <option value="">Select User</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.display_name}
                      </option>
                    ))}
                  </select>
                  <div className="form-help">
                    Select the user to assign this ticket to
                  </div>
                </div>
              ) : (
                <div className="form-group">
                  <label className="form-label">Issue Type *</label>
                  <select
                    value={newTicket.issue_type}
                    onChange={(e) => handleChange("issue_type", e.target.value)}
                    className="form-select"
                    required
                  >
                    <option value="">Select Issue Type</option>
                    {issueTypes.map((issue) => (
                      <option key={issue.value} value={issue.value}>
                        {issue.label}
                      </option>
                    ))}
                  </select>
                  <div className="form-help">
                    Ticket will be automatically assigned to the relevant team
                  </div>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  value={newTicket.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  placeholder="Describe the issue in detail"
                  rows="4"
                  className="form-textarea"
                />
              </div>

              <button type="submit" className="submit-button">
                Create Ticket
              </button>
            </form>
          </div>
        </div>

        <div className="order-info-container">
          <OrderInfo orderInfo={orderInfo} loading={orderLoading} />
        </div>
      </div>
    </div>
  );
};

const TicketDetailModal = ({
  ticketData,
  newMessage,
  setNewMessage,
  onAddMessage,
  onUpdateStatus,
  onDeleteTicket,
  onRefresh,
  onClose,
}) => {
  const { ticket, messages } = ticketData;

  const getStatusColor = (status) => {
    switch (status) {
      case "open":
        return "#ef4444";
      case "in_progress":
        return "#f59e0b";
      case "closed":
        return "#8b5cf6";
      case "resolved":
        return "#10b981";
      default:
        return "#6b7280";
    }
  };

  const getIssueTypeLabel = (issueType) => {
    const issueTypes = {
      tshirt_related_issues: "Tshirt Related Issues",
      dtf_related_issues: "DTF Related Issues",
      dtf_label_related_issues: "DTF Label Related Issues",
      tshirt_label_related_issues: "Tshirt Label Related Issues",
      other_issues: "Other Issues",
      special_assignment: "Special Assignment",
    };
    return issueTypes[issueType] || issueType;
  };

  const ticketDuration = calculateTicketDuration(
    ticket.created_at,
    ticket.resolved_at
  );

  const resolvedWithin24h = isResolvedWithin24h(
    ticket.created_at,
    ticket.resolved_at
  );
  const exceeded24h = isExceeded24h(ticket.created_at, ticket.status);

  const currentUserEmail = localStorage.getItem("email");
  const isTicketOwner = currentUserEmail === ticket.creator_email;
  const isAssignedUser = currentUserEmail === ticket.assigned_email;
  const isAdmin = ["mhmtmnctl@txmaya.com", "mhmtmnctl20@gmail.com"].includes(
    currentUserEmail
  );
  const canDelete = isTicketOwner || isAdmin;

  const getActionButton = () => {
    if (ticket.status === "resolved") {
      return null;
    }

    if (isAssignedUser) {
      if (ticket.status === "open") {
        return (
          <button
            onClick={() => onUpdateStatus(ticket.id, "in_progress")}
            className="tickets-action-button tickets-action-primary"
          >
            Start Working
          </button>
        );
      } else if (ticket.status === "in_progress") {
        return (
          <button
            onClick={() => onUpdateStatus(ticket.id, "closed")}
            className="tickets-action-button tickets-action-warning"
          >
            Mark as Closed
          </button>
        );
      }
    }

    if (isTicketOwner && ticket.status === "closed") {
      return (
        <button
          onClick={() => onUpdateStatus(ticket.id, "resolved")}
          className="tickets-action-button tickets-action-success"
        >
          Confirm Resolved
        </button>
      );
    }

    if (isAdmin) {
      if (ticket.status === "open") {
        return (
          <button
            onClick={() => onUpdateStatus(ticket.id, "in_progress")}
            className="tickets-action-button tickets-action-primary"
          >
            Start Working
          </button>
        );
      } else if (ticket.status === "in_progress") {
        return (
          <button
            onClick={() => onUpdateStatus(ticket.id, "closed")}
            className="tickets-action-button tickets-action-warning"
          >
            Mark as Closed
          </button>
        );
      } else if (ticket.status === "closed") {
        return (
          <button
            onClick={() => onUpdateStatus(ticket.id, "resolved")}
            className="tickets-action-button tickets-action-success"
          >
            Mark as Resolved
          </button>
        );
      }
    }

    return null;
  };

  return (
    <div className="tickets-modal-overlay">
      <div className="tickets-modal">
        <div className="tickets-modal-header">
          <div className="tickets-modal-title-section">
            <div className="tickets-modal-title-row">
              <h2>Order: {ticket.order_number}</h2>
              <div className="tickets-modal-header-actions">
                <button
                  onClick={onRefresh}
                  className="tickets-refresh-small-button"
                  title="Refresh messages"
                >
                  🔄
                </button>
                {canDelete && (
                  <button
                    onClick={() => onDeleteTicket(ticket.id)}
                    className="tickets-delete-small-button"
                    title="Delete ticket"
                  >
                    🗑️
                  </button>
                )}
              </div>
            </div>
            <div className="tickets-modal-badges">
              <span
                className="tickets-status-badge"
                style={{ backgroundColor: getStatusColor(ticket.status) }}
              >
                {ticket.status.replace("_", " ")}
              </span>
              <span className="tickets-category-badge">
                {getIssueTypeLabel(ticket.issue_type)}
              </span>
              {exceeded24h && (
                <span className="tickets-urgent-badge">24H+ URGENT</span>
              )}
              {ticket.status === "resolved" && !resolvedWithin24h && (
                <span className="tickets-warning-badge">24H+ Resolved</span>
              )}
              {ticketDuration && (
                <span className="tickets-duration-badge">
                  Resolved in: {ticketDuration}
                  {!resolvedWithin24h && " ⚠️"}
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="tickets-close-button">
            ×
          </button>
        </div>

        <div className="tickets-modal-info">
          <div className="tickets-info-item">
            <strong>Created by:</strong> {ticket.creator_first_name}{" "}
            {ticket.creator_last_name}
          </div>
          <div className="tickets-info-item">
            <strong>Assigned to:</strong> {ticket.assigned_first_name}{" "}
            {ticket.assigned_last_name}
          </div>
          <div className="tickets-info-item">
            <strong>Created:</strong>{" "}
            {new Date(ticket.created_at).toLocaleString()}
          </div>
          <div className="tickets-info-item">
            <strong>Last updated:</strong>{" "}
            {new Date(ticket.updated_at).toLocaleString()}
          </div>
        </div>

        <div className="tickets-modal-description">
          <h3>Description</h3>
          <p>{ticket.description}</p>
        </div>

        <div className="tickets-modal-actions">{getActionButton()}</div>

        <div className="tickets-modal-messages">
          <h3>Messages ({messages.length})</h3>

          <div className="tickets-messages-list">
            {messages.length === 0 ? (
              <div className="tickets-empty-state">
                <p>No messages yet</p>
              </div>
            ) : (
              messages.map((message) => (
                <div key={message.id} className="tickets-message">
                  <div className="tickets-message-header">
                    <strong>
                      {message.first_name} {message.last_name}
                    </strong>
                    <span>{new Date(message.created_at).toLocaleString()}</span>
                  </div>
                  <div className="tickets-message-content">
                    {message.message}
                  </div>
                </div>
              ))
            )}
          </div>

          {ticket.status !== "resolved" && (
            <div className="tickets-new-message">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message here..."
                rows="3"
                className="tickets-message-input"
              />
              <button onClick={onAddMessage} className="tickets-send-button">
                Send Message
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TicketsPage;
